import React, { useState } from 'react';
import { useList } from "@refinedev/core";
import { Card, Col, Row, Select, Statistic, Table, Typography, Tag, Button, message } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { DownloadOutlined } from "@ant-design/icons";
import { CHART_COLORS } from "../../utils/chartColors";
import { downloadCsvReport, downloadPdfReport } from "../../utils/reportExport";

const { Title } = Typography;

// Define types for inventory data
interface StockLevel {
    id: number;
    minimumQuantity: number;
    maximumQuantity: number;
    reorderPoint: number;
    currentStockLevel: number;
    reservedStock: number;
    availableStock: number;
    inventoryItemId: number;
    companyId: number;
}

interface Price {
    id: number;
    inventoryItemId: number;
    basePrice: number;
    effectivePrice: number;
    currencyCode: string;
    isActive: boolean;
}

interface InventoryItem {
    id: number;
    itemCode: string;
    itemName: string;
    itemTypeCode: string;
    stockLevel: StockLevel | null;
    prices: Price[];
    isActive: boolean;
    currencyTypeCode?: string;
    // These fields are added during processing for easier access
    unitPrice?: number;
    currentStock?: number;
    reorderPoint?: number;
    totalValue?: number;
}

interface CurrencyItem {
    id: string;
    currencyCode: string;
    name: string;
    symbol: string;
}

// Define chart data type
interface ChartData {
    name: string;
    value: number;
}

export const InventoryReport: React.FC = () => {
    // State for filters
    const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>();
    const [stockFilter, setStockFilter] = useState<string>('all'); // 'all', 'low', 'out'

    // Fetch inventory data
    const { data: inventoryData, isLoading: isLoadingInventory } = useList({
        resource: 'inventory',
    });

    // Fetch currencies for filter and symbol display
    const { data: currenciesData, isLoading: isLoadingCurrencies } = useList({
        resource: 'currency',
    });

    // Loading state
    if (isLoadingInventory || isLoadingCurrencies) {
        return <div>Loading...</div>;
    }

    // Extract inventory items from the data
    const inventoryItems = inventoryData?.data || [];

    // Process inventory items to add derived fields
    const processedItems = inventoryItems.map((item: InventoryItem) => {
        // Get the active price (first one or default to 0)
        const activePrice = item.prices.find(p => p.isActive) || item.prices[0];
        const unitPrice = activePrice ? activePrice.effectivePrice : 0;
        const currencyCode = activePrice ? activePrice.currencyCode : 'USD';

        // Get stock level info
        const currentStock = item.stockLevel ? item.stockLevel.currentStockLevel : 0;
        const reorderPoint = item.stockLevel ? item.stockLevel.reorderPoint : 0;

        // Calculate total value
        const totalValue = currentStock * unitPrice;

        return {
            ...item,
            unitPrice,
            currentStock,
            reorderPoint,
            totalValue,
            currencyTypeCode: currencyCode
        };
    });

    // Filter inventory items based on selected currency and stock filter
    const filteredItems = processedItems.filter((item: InventoryItem) => {
        // Currency filter
        if (selectedCurrency && item.currencyTypeCode !== selectedCurrency) {
            return false;
        }

        // Stock filter
        const currentStock = item.currentStock ?? 0;
        const reorderPoint = item.reorderPoint ?? 0;

        if (stockFilter === 'low') {
            return currentStock < reorderPoint && currentStock > 0;
        }
        if (stockFilter === 'out') {
            return currentStock === 0;
        }

        return true; // 'all' filter
    });

    // Calculate total items and low stock items
    const totalItems = filteredItems.length;
    const lowStockItems = filteredItems.filter((item: InventoryItem) =>
        (item.currentStock ?? 0) < (item.reorderPoint ?? 0) && (item.reorderPoint ?? 0) > 0
    ).length;
    const outOfStockItems = filteredItems.filter((item: InventoryItem) =>
        (item.currentStock ?? 0) === 0
    ).length;

    // Calculate total inventory value
    const totalInventoryValue = filteredItems.reduce(
        (sum: number, item: InventoryItem) => sum + ((item.currentStock ?? 0) * (item.unitPrice ?? 0)),
        0
    );

    // Process data by category for chart
    const inventoryByCategory = filteredItems.reduce((acc: Record<string, ChartData>, item: InventoryItem) => {
        const category = item.itemTypeCode || 'Uncategorized';
        if (!acc[category]) {
            acc[category] = {
                name: category,
                value: 0,
            };
        }
        acc[category].value += ((item.currentStock ?? 0) * (item.unitPrice ?? 0));
        return acc;
    }, {} as Record<string, ChartData>);

    // Convert the inventory by category object to an array for the pie chart
    const pieChartData: ChartData[] = Object.values(inventoryByCategory);

    const rowStatusLabel = (currentStock: number, reorderPoint: number) => {
        if (currentStock === 0) return "Out of Stock";
        const ratio = reorderPoint > 0 ? currentStock / reorderPoint : 1;
        if (ratio < 1) return "Low Stock";
        return "In Stock";
    };

    const exportSubtitleParts = [
        selectedCurrency ? `Currency: ${selectedCurrency}` : "Currency: All",
        `Stock filter: ${stockFilter}`,
    ];
    const exportSubtitle = exportSubtitleParts.join(" · ");

    const handleExportExcel = () => {
        const headers = [
            "Item Code",
            "Name",
            "Category",
            "Current Stock",
            "Reorder Point",
            "Status",
            "Currency",
            "Unit Price",
            "Total Value",
        ];
        const rows = filteredItems.map((item: InventoryItem) => {
            const cs = item.currentStock ?? 0;
            const rp = item.reorderPoint ?? 0;
            return [
                item.itemCode,
                item.itemName,
                item.itemTypeCode ?? "",
                cs,
                rp,
                rowStatusLabel(cs, rp),
                item.currencyTypeCode ?? "",
                (item.unitPrice ?? 0).toFixed(2),
                (cs * (item.unitPrice ?? 0)).toFixed(2),
            ];
        });
        downloadCsvReport("inventory-report", headers, rows);
        message.success("Inventory export downloaded (CSV opens in Excel).");
    };

    const handleExportPdf = async () => {
        const headers = [
            "Item Code",
            "Name",
            "Category",
            "Stock",
            "Reorder",
            "Status",
            "Cur.",
            "Price",
            "Value",
        ];
        const body = filteredItems.map((item: InventoryItem) => {
            const cs = item.currentStock ?? 0;
            const rp = item.reorderPoint ?? 0;
            return [
                item.itemCode,
                item.itemName,
                item.itemTypeCode ?? "",
                cs,
                rp,
                rowStatusLabel(cs, rp),
                item.currencyTypeCode ?? "",
                (item.unitPrice ?? 0).toFixed(2),
                (cs * (item.unitPrice ?? 0)).toFixed(2),
            ];
        });
        try {
            await downloadPdfReport("Inventory report", exportSubtitle, headers, body);
            message.success("PDF downloaded.");
        } catch {
            message.error("Could not generate PDF.");
        }
    };

    // Define columns for the inventory table
    const columns: ColumnType<InventoryItem>[] = [
        {
            title: 'Item Code',
            dataIndex: 'itemCode',
            key: 'itemCode',
        },
        {
            title: 'Name',
            dataIndex: 'itemName',
            key: 'name',
        },
        {
            title: 'Category',
            dataIndex: 'itemTypeCode',
            key: 'category',
            filters: Array.from(new Set(inventoryItems.map((item: InventoryItem) => item.itemTypeCode || 'Uncategorized')))
                .map((category) => ({ text: category as React.ReactNode, value: category as string })),
            onFilter: (value, record: InventoryItem) => {
                if (typeof value === 'string') {
                    return record.itemTypeCode === value || (!record.itemTypeCode && value === 'Uncategorized');
                }
                return false;
            },
        },
        {
            title: 'Current Stock',
            key: 'currentStock',
            render: (_: unknown, record: InventoryItem) => record.currentStock ?? 0,
            sorter: (a: InventoryItem, b: InventoryItem) => (a.currentStock ?? 0) - (b.currentStock ?? 0),
        },
        {
            title: 'Reorder Point',
            key: 'reorderPoint',
            render: (_: unknown, record: InventoryItem) => record.reorderPoint ?? 0,
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: unknown, record: InventoryItem) => {
                const currentStock = record.currentStock ?? 0;
                const reorderPoint = record.reorderPoint ?? 0;
                const stockLevel = reorderPoint > 0 ? currentStock / reorderPoint : 1;
                let color, status;

                if (currentStock === 0) {
                    color = 'red';
                    status = 'Out of Stock';
                } else if (stockLevel < 1) {
                    color = 'orange';
                    status = 'Low Stock';
                } else {
                    color = 'green';
                    status = 'In Stock';
                }

                return <Tag color={color}>{status}</Tag>;
            },
            filters: [
                { text: 'In Stock', value: 'in' },
                { text: 'Low Stock', value: 'low' },
                { text: 'Out of Stock', value: 'out' },
            ],
            onFilter: (value, record: InventoryItem) => {
                const currentStock = record.currentStock ?? 0;
                const reorderPoint = record.reorderPoint ?? 0;

                if (typeof value === 'string') {
                    if (value === 'out') return currentStock === 0;
                    if (value === 'low') return currentStock < reorderPoint && currentStock > 0;
                    if (value === 'in') return currentStock >= reorderPoint;
                }
                return true;
            },
        },
        {
            title: 'Unit Price',
            key: 'unitPrice',
            render: (_: unknown, record: InventoryItem) => {
                const symbol = currenciesData?.data?.find((c: CurrencyItem) => c.currencyCode === record.currencyTypeCode)?.symbol || '$';
                return `${symbol}${(record.unitPrice ?? 0).toFixed(2)}`;
            },
            sorter: (a: InventoryItem, b: InventoryItem) => (a.unitPrice ?? 0) - (b.unitPrice ?? 0),
        },
        {
            title: 'Total Value',
            key: 'totalValue',
            render: (_: unknown, record: InventoryItem) => {
                const symbol = currenciesData?.data?.find((c: CurrencyItem) => c.currencyCode === record.currencyTypeCode)?.symbol || '$';
                const totalValue = (record.currentStock ?? 0) * (record.unitPrice ?? 0);
                return `${symbol}${totalValue.toFixed(2)}`;
            },
            sorter: (a: InventoryItem, b: InventoryItem) => {
                const totalValueA = (a.currentStock ?? 0) * (a.unitPrice ?? 0);
                const totalValueB = (b.currentStock ?? 0) * (b.unitPrice ?? 0);
                return totalValueA - totalValueB;
            },
        },
    ];

    return (
        <div className="space-y-4">
            <Title level={2}>Inventory Report</Title>

            <Card>
                <div className="flex flex-wrap gap-4 mb-4">
                    <Select
                        style={{ width: 200 }}
                        placeholder="Select Currency"
                        allowClear
                        onChange={(value: string | undefined) => setSelectedCurrency(value)}
                        options={currenciesData?.data?.map((currency: CurrencyItem) => ({
                            value: currency.currencyCode,
                            label: `${currency.name} (${currency.currencyCode})`,
                        }))}
                    />
                    <Select
                        style={{ width: 200 }}
                        placeholder="Stock Filter"
                        value={stockFilter}
                        onChange={(value: string) => setStockFilter(value)}
                        options={[
                            { value: 'all', label: 'All Stock' },
                            { value: 'low', label: 'Low Stock' },
                            { value: 'out', label: 'Out of Stock' },
                        ]}
                    />
                    <div style={{ marginLeft: 'auto' }}>
                        <Button type="primary" icon={<DownloadOutlined />} style={{ marginRight: "8px" }} onClick={handleExportExcel}>
                            Export to Excel
                        </Button>
                        <Button icon={<DownloadOutlined />} onClick={() => void handleExportPdf()}>
                            Export to PDF
                        </Button>
                    </div>
                </div>

                <Row gutter={16}>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Total Items"
                                value={totalItems}
                                precision={0}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Low Stock Items"
                                value={lowStockItems}
                                precision={0}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Out of Stock Items"
                                value={outOfStockItems}
                                precision={0}
                                valueStyle={{ color: '#ff4d4f' }}
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>

            <Row gutter={16}>
                <Col span={16}>
                    <Card title="Inventory Details">
                        <Table
                            columns={columns}
                            dataSource={filteredItems}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            scroll={{ x: 1200 }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card title={`Inventory Value by Category (${selectedCurrency || 'All Currencies'})`}>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={pieChartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    label
                                >
                                    {pieChartData.map((_, index) => (
                                        <Cell key={`slice-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value: number) => {
                                    const symbol = selectedCurrency
                                        ? currenciesData?.data?.find((c: CurrencyItem) => c.currencyCode === selectedCurrency)?.symbol || '$'
                                        : '$';
                                    return `${symbol}${Number(value).toFixed(2)}`;
                                }} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>

            {lowStockItems > 0 && (
                <Card>
                    <div className="warning-alert" style={{
                        padding: '12px 16px',
                        backgroundColor: '#fffbe6',
                        border: '1px solid #ffe58f',
                        borderRadius: '2px',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ color: '#faad14', marginRight: '8px' }}>⚠️</span>
                            <div>
                                <div style={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.85)' }}>Low Stock Warning</div>
                                <div style={{ color: 'rgba(0, 0, 0, 0.65)' }}>
                                    You have {lowStockItems} item(s) that are below the reorder point. Consider restocking these items soon.
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            <Card title="Summary">
                <Row gutter={16}>
                    <Col span={6}>
                        <Statistic
                            title="Total Inventory Value"
                            value={totalInventoryValue}
                            precision={2}
                            prefix={selectedCurrency ? currenciesData?.data?.find((c: CurrencyItem) => c.currencyCode === selectedCurrency)?.symbol || '$' : '$'}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Total Items"
                            value={totalItems}
                            precision={0}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Items Needing Reorder"
                            value={lowStockItems}
                            precision={0}
                            valueStyle={{ color: lowStockItems > 0 ? '#faad14' : '#3f8600' }}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Out of Stock"
                            value={outOfStockItems}
                            precision={0}
                            valueStyle={{ color: outOfStockItems > 0 ? '#ff4d4f' : '#3f8600' }}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default InventoryReport;